import { useState, useEffect } from 'react';
import {
  Box, Typography, TextField, Button, CircularProgress,
  Paper, InputAdornment, Alert,
} from '@mui/material';
import {
  Save as SaveIcon,
  CheckCircle as CheckIcon,
  Cancel as CancelIcon,
} from '@mui/icons-material';
import api from '../config/api';
import { useTranslation } from 'react-i18next';
import useStore from '../config/store';
import { slugify } from '../jobBoard/slugUtils';

// Dominio base de los links públicos en producción. Mantener alineado con
// BASE_DOMAIN de src/jobBoard/hostSlug.js y con PUBLIC_BASE_DOMAIN del backend.
const BASE_DOMAIN = 'amandahr.ai';

// Ejemplo de link de test con el subdominio de la org (solo para mostrar).
const testPreviewUrl = (slug) =>
  `https://${slug || '...'}.${BASE_DOMAIN}/disc-test/…`;

/**
 * URL personalizada — NIVEL ORGANIZACIÓN (modo evaluaciones).
 * Configura el subdominio con el que los candidatos reciben los links de
 * tests (DISC, razonamiento, habilidades, ventas) y de solicitud de
 * documentos: {slug}.amandahr.ai en vez de app.amandahr.ai.
 *
 * IMPORTANTE: es el MISMO slug de la web de empleos del ATS (un solo dato,
 * dos lectores). Aquí solo se edita el slug; el resto de la config del board
 * (habilitado, nombre, banner) no se toca. Backend: /organizations/public-slug.
 */
const CustomUrlTab = () => {
  const { t } = useTranslation();
  const { organization, authToken, showAlert } = useStore.getState();

  const [slug, setSlug] = useState('');
  const [savedSlug, setSavedSlug] = useState(null);
  const [boardEnabled, setBoardEnabled] = useState(false);
  const [slugCheck, setSlugCheck] = useState(null); // null | 'checking' | {available, message}
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    const load = async () => {
      if (!organization) { setLoading(false); return; }
      try {
        setLoading(true);
        const res = await api.get(`/organizations/public-slug`, {
          params: { organizationId: organization },
          headers: { Authorization: `Bearer ${authToken}` },
        });
        const data = res.data.response?.data;
        if (data) {
          setSlug(data.slug || '');
          setSavedSlug(data.slug || null);
          setBoardEnabled(!!data.boardEnabled);
        }
      } catch (err) {
        console.error(err);
        showAlert(t('configuration.customUrl.loadError'), 'error');
      } finally {
        setLoading(false);
      }
    };
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Reutiliza el mismo chequeo de disponibilidad que el tab del ATS
  // (mismo slug, misma colección espejo en el backend).
  const handleCheckSlug = async () => {
    const normalized = slugify(slug);
    setSlug(normalized);
    if (!normalized) return;
    try {
      setSlugCheck('checking');
      const res = await api.get(`/organizations/ats-job-board/check-slug`, {
        params: { organizationId: organization, slug: normalized },
        headers: { Authorization: `Bearer ${authToken}` },
      });
      setSlugCheck(res.data.response || null);
    } catch (err) {
      console.error(err);
      setSlugCheck(null);
    }
  };

  const handleSave = async () => {
    const normalized = slugify(slug);
    if (boardEnabled && !normalized) {
      showAlert(t('configuration.customUrl.slugRequiredBoard'), 'error');
      return;
    }
    try {
      setSaving(true);
      const res = await api.put(
        `/organizations/public-slug`,
        { slug: normalized || null },
        {
          params: { organizationId: organization },
          headers: { Authorization: `Bearer ${authToken}` },
        }
      );
      if (res.data.response?.status_response === 'OK') {
        const saved = res.data.response.data?.slug || null;
        setSavedSlug(saved);
        setSlug(saved || '');
        setSlugCheck(null);
        showAlert(t('configuration.customUrl.saveSuccess'), 'success');
      } else {
        showAlert(t('configuration.customUrl.saveError'), 'error');
      }
    } catch (err) {
      console.error(err);
      if (err.response?.status === 409) {
        showAlert(t('configuration.customUrl.slugTaken'), 'error');
      } else {
        showAlert(t('configuration.customUrl.saveError'), 'error');
      }
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ maxWidth: 560 }}>
      <Typography variant="h6" fontWeight={700} sx={{ mb: 0.5 }}>
        {t('configuration.customUrl.title')}
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
        {t('configuration.customUrl.description')}
      </Typography>

      {/* Aviso: mismo slug que la web de empleos del ATS */}
      <Alert severity="info" sx={{ mb: 2 }}>
        {t('configuration.customUrl.sharedNote')}
      </Alert>

      <TextField
        fullWidth
        label={t('configuration.customUrl.slugLabel')}
        value={slug}
        onChange={(e) => { setSlug(e.target.value); setSlugCheck(null); }}
        onBlur={handleCheckSlug}
        helperText={
          slugCheck === 'checking'
            ? t('configuration.customUrl.checking')
            : slugCheck && !slugCheck.available
              ? t('configuration.customUrl.slugTaken')
              : t('configuration.customUrl.slugHint')
        }
        error={!!(slugCheck && slugCheck !== 'checking' && !slugCheck.available)}
        InputProps={{
          endAdornment: (
            <InputAdornment position="end">
              {slugCheck === 'checking' && <CircularProgress size={16} />}
              {slugCheck && slugCheck !== 'checking' && slugCheck.available && <CheckIcon color="success" fontSize="small" />}
              {slugCheck && slugCheck !== 'checking' && !slugCheck.available && <CancelIcon color="error" fontSize="small" />}
            </InputAdornment>
          ),
        }}
        sx={{ mb: 1.5 }}
      />

      {/* Vista previa del link que recibirá el candidato */}
      <Paper variant="outlined" sx={{ p: 1.5, mb: 1.5, backgroundColor: '#f9f9f9' }}>
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.25 }}>
          {t('configuration.customUrl.previewLabel')}
        </Typography>
        <Typography variant="body2" sx={{ wordBreak: 'break-all', fontFamily: 'monospace' }}>
          {testPreviewUrl(slugify(slug))}
        </Typography>
      </Paper>

      {!slugify(slug) && (
        <Alert severity="warning" sx={{ mb: 1.5 }}>
          {t('configuration.customUrl.noSlugNote')}
        </Alert>
      )}

      {savedSlug && slugify(slug) !== savedSlug && (
        <Alert severity="warning" sx={{ mb: 1.5 }}>
          {t('configuration.customUrl.changeWarning', { old: savedSlug })}
        </Alert>
      )}

      <Button
        variant="contained"
        startIcon={saving ? <CircularProgress size={16} color="inherit" /> : <SaveIcon />}
        onClick={handleSave}
        disabled={saving}
        sx={{ mt: 0.5, backgroundColor: '#6A5AF9', '&:hover': { backgroundColor: '#5B4DE8' } }}
      >
        {t('configuration.customUrl.save')}
      </Button>
    </Box>
  );
};

export default CustomUrlTab;
